import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { ICONS } from 'src/constants/icons';
import { SCREENS } from 'src/constants/screens';
import { appStore } from 'src/mobx/appStore';
import { cartStore } from 'src/mobx/cartStore';
import { localeStore } from 'src/mobx/localesStore';
import { IProduct } from 'src/types/products';
import { formatPrice, formatUnit, getTranslate } from 'src/utils/formatter';
import { calculateProductPrice } from 'src/utils/math';
import Icon from 'src/view/components/Controls/Icon/Icon';
import Quantity from 'src/view/components/Controls/Quantity/Quantity';
import Image from 'src/view/components/Image/Image';
import ResponsiveContent from 'src/view/components/ResponsiveContent/ResponsiveContent';
import Donation from 'src/view/components/Tooltip/Donation/Donation';
import CartProductExclusions from './components/CartProductExclusions/CartProductExclusions';
import CartProductItems from './components/CartProductItems/CartProductItems';
import CartProductModifiers from './components/CartProductModifiers/CartProductModifiers';
import './style.scss';

export interface ICartProductProps {
  product: IProduct;
  index: number;
}

@observer
export default class CartProduct extends React.Component<ICartProductProps> {
  get cashbackAmount() {
    const { product } = this.props;
    // NOTE: MVP Combo has no cashback
    if (product?.type) return 0;
    const percent = appStore.settings?.cashback?.percent || 0;
    const _price = product ? calculateProductPrice(product) : 0;

    const cashback = (_price * percent) / 100;
    return Number(cashback.toFixed(2));
  }

  removeProductFromCart = () => {
    const { product } = this.props;
    cartStore.remove(product);
  };

  changeQuantity = (quantity: number) => {
    this.props.product.quantity = quantity;
    cartStore.update(this.props.product);
  };

  handleProductClick = (event: any) => {
    if (event.target.classList?.contains('demo-icon') || event.target?.tagName === 'INPUT') {
      event.preventDefault();
    }
  };

  public render() {
    const { product } = this.props;
    const _price = calculateProductPrice(product);

    return (
      <Link
        rel="tag"
        title={getTranslate(product.name)}
        hrefLang={localeStore.language}
        onClick={this.handleProductClick}
        className={`d-flex flex-column ${product.error || !product.orderable ? 'cart-product-invalid' : ''}`}
        to={{
          pathname: cartStore.isSMS ? SCREENS.SMS_PRODUCT(product.id) : SCREENS.PRODUCT(product.id),
          search: `?language=${localeStore.language}`,
          state: {
            product: toJS(product),
            updateProduct: true,
          },
        }}
      >
        <div className={`cart-product d-flex flex-column mb-3`}>
          <div className="d-flex flex-row align-items-start mb-3 ">
            <ResponsiveContent>
              <Image
                wrapperClassName="cart-product-image mr-3"
                src={product.image}
                aspectRatio={1.5}
                title={getTranslate(product.name)}
                alt={getTranslate(product.name)}
              />
            </ResponsiveContent>

            <div className="product-title__wrapper d-flex flex-grow-1 flex-column full-height">
              <div className="d-flex flex-column full-width">
                <div className="d-flex flex-column full-width">
                  {product.error ? (
                    <div className="product-error-container">
                      <p>{product.error}</p>
                    </div>
                  ) : null}
                  <h2 className="color-dark text-size-18 text-md-size-16 family-semibold mb-1 product-title text-lite-elipsis">
                    {getTranslate(product.name)} {/* Size */}
                    {product.size ? (
                      <span
                        className={`text-size-18 text-md-size-16 family-semibold  ${
                          product.size.error ? 'size-invalid' : ''
                        }`}
                      >
                        {getTranslate(product.size.name)}
                      </span>
                    ) : null}
                    {/* Show Product quantity near Product Name */}
                    {!cartStore.isEditable ? (
                      <bdo
                        className="color-gray text-size-18 text-md-size-16 family-regular pl-1"
                        dir={localeStore.direction}
                      >
                        {`x${product.quantity}`}
                      </bdo>
                    ) : null}
                  </h2>
                </div>
                {product.is_donation ? (
                  <div className="mr-3 d-flex flex-row align-items-center donation-container">
                    <Donation />
                    <p className="color-dark text-size-12 ml-3">{localeStore.t('cart_donation_info')}</p>
                  </div>
                ) : null}
              </div>
              <div className="d-flex flex-row align-items-center">
                <p className="text-size-14 text-md-size-12 color-gray family-semibold">
                  {product.size
                    ? formatUnit(product.size.unit, product.size.weight)
                    : formatUnit(product.unit, product.weight)}
                </p>
              </div>
            </div>

            <div className="d-flex flex-row flex-sm-column align-items-center align-items-sm-end ">
              <div className="d-flex flex-column align-items-end">
                {product.orderable && !product.error ? (
                  <p className="color-primary text-size-20 text-md-size-14 family-semibold mb-0  mb-sm-1 text-nowrap product-price mr-0">
                    {formatPrice(_price)}
                  </p>
                ) : null}
              </div>

              {cartStore.isEditable ? (
                <div className="d-none d-lg-flex flex-row align-items-center product-controls ">
                  {!product.error ? (
                    <Link
                      rel="tag"
                      title={getTranslate(product.name)}
                      hrefLang={localeStore.language}
                      to={{
                        pathname: cartStore.isSMS ? SCREENS.SMS_PRODUCT(product.id) : SCREENS.PRODUCT(product.id),
                        search: `?language=${localeStore.language}`,
                        state: {
                          product: toJS(product),
                          updateProduct: true,
                        },
                      }}
                    >
                      <Icon
                        name={ICONS.PENCIL}
                        className="cart-product-icon px-2 py-2 text-size-14 color-gray color_hover-primary background_hover-light mr-2"
                      />
                    </Link>
                  ) : null}

                  <Icon
                    onPress={this.removeProductFromCart}
                    name={ICONS.CLOSE}
                    className="cart-product-icon py-2 pl-2 px-lg-2 py-lg-2 text-size-14 color-gray color_hover-primary background_hover-light"
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row flex-md-column flex-lg-row product-modifiers-container">
            <div className="d-flex order-2 order-sm-1 flex-column mr-3 order-md-2 order-lg-1">
              {cartStore.isEditable ? <Quantity onChange={this.changeQuantity} value={product.quantity} /> : null}
            </div>
            <div className="d-flex order-1 order-sm-2 flex-grow-1 flex-column order-md-1 order-lg-2 modifiers-container">
              {/* Modifiers */}
              <CartProductModifiers product={product} />
              {/* Exclusions */}
              <CartProductExclusions exclusions={product.exclusions} />
              {/* Items(only in combo) */}
              <CartProductItems product={product} />
            </div>
          </div>
        </div>
      </Link>
    );
  }
}
